/*******************************************************
* Form Autocomplete
* 
* A reusable form autocomplete component that wraps MUI Autocomplete with form-specific
* functionality including error handling and value management.
* 
* Key Features:
* - Integrated form field error handling
* - Standardized onChange handling
* - MUI Autocomplete integration with consistent styling
* - Support for option objects with label/value pattern
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import React from 'react';
import {
    Autocomplete,
    type AutocompleteProps,
    type TextFieldProps
} from '@mui/material';
import { FormTextField } from './FormTextField';
import FormSelect from './FormSelect';
import { sortOptionsByLabel } from './formSelectSearchUtils';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
export interface OptionType {
    ID: string | number;
    Label: string;
    [key: string]: unknown;
}

interface FormAutocompleteProps<T> extends Omit<AutocompleteProps<T, false, boolean, false>, 'renderInput' | 'onChange'> {
    name: string;
    label: string;
    value: T | null;
    onChange: (field: string, value: T | null) => void;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    margin?: TextFieldProps['margin'];
    textFieldProps?: Partial<Omit<TextFieldProps, 'onChange' | 'error'>>;
    /** Use FormSelect's mobile drawer. Disable on public surfaces without app providers. */
    useMobileSelect?: boolean;
    /** Sort options A-Z by label (default: true). Set false only when the list has a meaningful fixed order. */
    sortOptions?: boolean;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
//#region CONSTANT - STYLES
//#endregion
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
export const FormAutocomplete = <T extends OptionType>({
    name,
    label,
    value,
    onChange,
    error,
    required = false,
    disabled = false,
    fullWidth = true,
    margin = "dense",
    disableClearable = false,
    options,
    getOptionLabel = (option) => option.Label || '',
    textFieldProps = {},
    useMobileSelect = true,
    sortOptions = true,
    ...rest
}: FormAutocompleteProps<T>) => {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    const handleChange = (_event: React.SyntheticEvent, newValue: T | null) => {
        onChange(name, newValue);
    };
    //#endregion

    const renderInput: NonNullable<AutocompleteProps<T, false, boolean, false>['renderInput']> = (params) => (
        <FormTextField
            {...params}
            name={name}
            margin={margin}
            required={required}
            fullWidth={fullWidth}
            label={label}
            error={error}
            helperText={error}
            disabled={disabled}
            slotProps={{
                inputLabel: {
                    sx: { color: 'text.primary' }
                }
            }}
            {...textFieldProps}
        />
    );

    //########## MARKDOWN ##########
    // #region MARKDOWN
    if (!useMobileSelect) {
        return (
            <Autocomplete
                id={name}
                options={sortOptions ? sortOptionsByLabel(options, getOptionLabel, rest.groupBy) : options}
                getOptionLabel={getOptionLabel}
                value={value}
                disabled={disabled}
                disableClearable={disableClearable}
                onChange={handleChange}
                renderInput={renderInput}
                {...rest}
            />
        );
    }

    return (
        <FormSelect
            id={name}
            dataSource={[...options]}
            sortOptions={sortOptions}
            getOptionLabel={getOptionLabel}
            value={value}
            disabled={disabled}
            disableClearable={disableClearable}
            onChange={handleChange}
            renderInput={renderInput}
            {...rest}
        />
    );
    //#endregion
};
